import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  MessageCircle,
  RefreshCw,
  Mail,
  Phone,
  Eye,
  Archive,
  Reply,
  CheckCircle2,
  Clock,
  Filter,
  Search,
  X,
  User,
  Tag
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const MESSAGE_CATEGORIES = [
  { value: 'prayer_request', label: 'Prayer Request', color: 'purple', icon: '🙏' },
  { value: 'counselling', label: 'Counselling', color: 'blue', icon: '💬' },
  { value: 'testimony', label: 'Testimony', color: 'emerald', icon: '✨' },
  { value: 'membership', label: 'Membership', color: 'amber', icon: '👥' },
  { value: 'giving', label: 'Giving', color: 'green', icon: '💝' },
  { value: 'general_inquiry', label: 'General Inquiry', color: 'slate', icon: '❓' },
  { value: 'feedback', label: 'Feedback', color: 'indigo', icon: '📝' }
];

const MESSAGE_STATUSES = [
  { value: 'new', label: 'New', color: 'blue' },
  { value: 'read', label: 'Read', color: 'amber' },
  { value: 'replied', label: 'Replied', color: 'emerald' },
  { value: 'archived', label: 'Archived', color: 'slate' }
];

const ContactMessagesSection = ({ adminUser }) => {
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, read: 0, replied: 0 });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [internalNotes, setInternalNotes] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [messagesRes, statsRes] = await Promise.all([
        axios.get(`${API}/contact-messages`, {
          params: { status: filterStatus, category: filterCategory }
        }),
        axios.get(`${API}/contact-messages/stats`)
      ]);
      setMessages(messagesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
    setLoading(false);
  }, [filterStatus, filterCategory]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleStatusChange = async (messageId, newStatus) => {
    try {
      await axios.put(`${API}/contact-messages/${messageId}`, {
        status: newStatus,
        replied_by: adminUser?.full_name || ''
      });
      loadData();
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleSaveNotes = async (messageId) => {
    try {
      await axios.put(`${API}/contact-messages/${messageId}`, {
        internal_notes: internalNotes
      });
      loadData();
      setSelectedMessage(prev => ({ ...prev, internal_notes: internalNotes }));
    } catch (error) {
      console.error('Error saving notes:', error);
    }
  };

  const handleOpenMessage = async (message) => {
    setSelectedMessage(message);
    setInternalNotes(message.internal_notes || '');
    
    // Mark as read if new
    if (message.status === 'new') {
      await handleStatusChange(message.id, 'read');
    }
  };

  const filteredMessages = messages.filter(m => 
    m.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.message?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryInfo = (category) => {
    return MESSAGE_CATEGORIES.find(c => c.value === category) || MESSAGE_CATEGORIES[5];
  };

  const getStatusBadge = (status) => {
    const s = MESSAGE_STATUSES.find(st => st.value === status) || MESSAGE_STATUSES[0];
    const colorMap = {
      blue: 'bg-blue-100 text-blue-700 border-blue-200',
      amber: 'bg-amber-100 text-amber-700 border-amber-200',
      emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      slate: 'bg-slate-100 text-slate-700 border-slate-200'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${colorMap[s.color]}`}>
        {s.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="contact-messages-section">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-violet-500">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Connect Messages</h2>
              <p className="text-sm text-slate-500">Manage inquiries from the "Connect With Us" form</p>
            </div>
          </div>
          <motion.button
            onClick={loadData}
            className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-all flex items-center gap-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </motion.button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200">
          <p className="text-sm text-slate-600">Total</p>
          <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
        </div>
        <div className="p-4 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
            <p className="text-sm text-blue-700">New</p>
          </div>
          <p className="text-2xl font-bold text-blue-900">{stats.new}</p>
        </div>
        <div className="p-4 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200">
          <p className="text-sm text-amber-700">Read</p>
          <p className="text-2xl font-bold text-amber-900">{stats.read}</p>
        </div>
        <div className="p-4 rounded-2xl bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200">
          <p className="text-sm text-emerald-700">Replied</p>
          <p className="text-2xl font-bold text-emerald-900">{stats.replied}</p>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-2xl p-4 shadow-lg border border-slate-100 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search messages..."
            className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-purple-400 outline-none"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Status Filter */}
        <div className="flex flex-wrap gap-2">
          <span className="text-sm text-slate-600 flex items-center gap-1 mr-2">
            <Filter className="w-4 h-4" />
            Status:
          </span>
          <button
            onClick={() => setFilterStatus('')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              !filterStatus ? 'bg-purple-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            All
          </button>
          {MESSAGE_STATUSES.map(status => (
            <button
              key={status.value}
              onClick={() => setFilterStatus(status.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                filterStatus === status.value
                  ? 'bg-purple-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {status.label}
            </button>
          ))}
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2">
          <span className="text-sm text-slate-600 flex items-center gap-1 mr-2">
            <Tag className="w-4 h-4" />
            Category:
          </span>
          <button
            onClick={() => setFilterCategory('')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              !filterCategory ? 'bg-purple-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            All
          </button>
          {MESSAGE_CATEGORIES.map(cat => (
            <button
              key={cat.value}
              onClick={() => setFilterCategory(cat.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                filterCategory === cat.value
                  ? 'bg-purple-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {cat.icon} {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Messages List */}
      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-lg border border-slate-100">
            <RefreshCw className="w-8 h-8 mx-auto animate-spin text-purple-500" />
          </div>
        ) : filteredMessages.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-lg border border-slate-100">
            <MessageCircle className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-600 font-medium">No messages found</p>
            <p className="text-sm text-slate-500 mt-1">Messages from the "Connect With Us" form will appear here</p>
          </div>
        ) : (
          filteredMessages.map((message, index) => {
            const categoryInfo = getCategoryInfo(message.category);
            return (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                onClick={() => handleOpenMessage(message)}
                className={`bg-white rounded-xl p-4 shadow-lg border cursor-pointer transition-all hover:shadow-xl ${
                  message.status === 'new' 
                    ? 'border-blue-300 bg-gradient-to-r from-blue-50/50 to-white' 
                    : 'border-slate-100'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    <div className={`p-2 rounded-xl shrink-0 ${
                      message.status === 'new' ? 'bg-blue-100' : 'bg-slate-100'
                    }`}>
                      <span className="text-lg">{categoryInfo.icon}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-slate-800">{message.full_name}</span>
                        {getStatusBadge(message.status)}
                        <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">
                          {categoryInfo.label}
                        </span>
                      </div>
                      <p className="font-medium text-slate-700">{message.subject}</p>
                      <p className="text-sm text-slate-500 truncate">{message.message}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                        {message.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {message.email}
                          </span>
                        )}
                        {message.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {message.phone}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(message.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Eye className="w-5 h-5 text-slate-400 shrink-0" />
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Message Detail Modal */}
      <AnimatePresence>
        {selectedMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedMessage(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="p-6 border-b border-slate-100">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusBadge(selectedMessage.status)}
                      <span className="px-2 py-1 rounded-full text-xs bg-slate-100 text-slate-600">
                        {getCategoryInfo(selectedMessage.category).icon} {getCategoryInfo(selectedMessage.category).label}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-800">{selectedMessage.subject}</h3>
                  </div>
                  <button 
                    onClick={() => setSelectedMessage(null)} 
                    className="p-2 hover:bg-slate-100 rounded-lg"
                  >
                    <X className="w-5 h-5 text-slate-500" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Sender Info */}
                <div className="p-4 rounded-xl bg-slate-50 space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-purple-100">
                      <User className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">{selectedMessage.full_name}</p>
                      <p className="text-sm text-slate-500">Sent {formatDate(selectedMessage.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex gap-4 text-sm">
                    {selectedMessage.email && (
                      <a href={`mailto:${selectedMessage.email}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                        <Mail className="w-4 h-4" />
                        {selectedMessage.email}
                      </a>
                    )}
                    {selectedMessage.phone && (
                      <a href={`tel:${selectedMessage.phone}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                        <Phone className="w-4 h-4" />
                        {selectedMessage.phone}
                      </a>
                    )}
                  </div>
                </div>

                {/* Message Content */}
                <div>
                  <h4 className="font-semibold text-slate-700 mb-2">Message</h4>
                  <div className="p-4 rounded-xl bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-100">
                    <p className="text-slate-700 whitespace-pre-wrap">{selectedMessage.message}</p>
                  </div>
                </div>

                {/* Internal Notes */}
                <div>
                  <h4 className="font-semibold text-slate-700 mb-2">Internal Notes</h4>
                  <textarea
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-purple-400 outline-none"
                    rows={3}
                    placeholder="Add internal notes (not visible to sender)..."
                    value={internalNotes}
                    onChange={e => setInternalNotes(e.target.value)}
                  />
                  <motion.button
                    onClick={() => handleSaveNotes(selectedMessage.id)}
                    className="mt-2 px-4 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Save Notes
                  </motion.button>
                </div>

                {/* Reply Info */}
                {selectedMessage.status === 'replied' && selectedMessage.replied_at && (
                  <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="font-medium">Replied by {selectedMessage.replied_by}</span>
                    </div>
                    <p className="text-sm text-emerald-600 mt-1">{formatDate(selectedMessage.replied_at)}</p>
                  </div>
                )}
              </div>

              {/* Modal Footer - Actions */}
              <div className="p-6 border-t border-slate-100 bg-slate-50">
                <div className="flex flex-wrap gap-3">
                  {selectedMessage.status !== 'replied' && (
                    <motion.button
                      onClick={() => handleStatusChange(selectedMessage.id, 'replied')}
                      className="flex-1 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 text-white font-semibold flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Reply className="w-5 h-5" />
                      Mark as Replied
                    </motion.button>
                  )}
                  {selectedMessage.status !== 'archived' && (
                    <motion.button
                      onClick={() => handleStatusChange(selectedMessage.id, 'archived')}
                      className="px-6 py-3 rounded-xl bg-slate-200 text-slate-700 font-medium flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Archive className="w-5 h-5" />
                      Archive
                    </motion.button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContactMessagesSection;
